import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { DayPicker } from "react-day-picker";
import { X, CalendarCheck2, Clock, CheckCircle2, ArrowLeft, ArrowRight } from "lucide-react";
import "react-day-picker/dist/style.css";
import { useSite } from "@/context/SiteContext";
import { api, formatApiErrorDetail, track } from "@/lib/api";

const SERVICES = [
  "E-commerce Support",
  "Real Estate Operations",
  "Appointment Setting",
  "Cold Calling & Phone Support",
  "Administrative & Executive Assistance",
  "Social Media Management & Content",
];

function slotLabel(t) {
  const [h, m] = t.split(":").map(Number);
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

export default function BookingModal() {
  const { bookingOpen, setBookingOpen, bookingPkg, bookingType, content } = useSite();
  const [step, setStep] = useState(0);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "", service: "", message: "" });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const availability = content.availability || {};
  const blocked = useMemo(() => new Set((availability.blocked_dates || []).map((d) => d.slice(0, 10))), [availability]);
  const slots = useMemo(() => {
    if (availability.time_slots?.length) return availability.time_slots;
    return Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`);
  }, [availability]);

  useEffect(() => {
    if (bookingOpen) {
      setStep(0); setDate(null); setTime(""); setDone(false); setError("");
      setForm((f) => ({ ...f, service: bookingPkg ? "" : f.service }));
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [bookingOpen, bookingPkg]);

  if (!bookingOpen) return null;

  const isQuote = bookingType === "quote";

  const disabledDays = [
    { before: new Date(new Date().setHours(0, 0, 0, 0)) },
    (d) => blocked.has(d.toISOString().slice(0, 10)),
  ];

  const submit = async () => {
    setError("");
    if (!form.name || !form.email) { setError("Please fill in your name and email."); return; }
    if (!isQuote && (!date || !time)) { setError("Please pick a date and time."); return; }
    setBusy(true);
    try {
      await api.post("/leads", {
        type: isQuote ? "quote" : "booking",
        ...form,
        package: bookingPkg || "",
        date: date ? date.toISOString().slice(0, 10) : "",
        time,
      });
      track("booking_submitted");
      setDone(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const canNext = form.name && form.email && (isQuote || form.service);

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 bg-[#05081C]/85 backdrop-blur-sm" data-testid="booking-modal-backdrop">
      <motion.div
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-4xl rounded-3xl overflow-hidden bg-white text-[#0F172A] shadow-2xl max-h-[92vh] overflow-y-auto"
        data-testid="booking-modal"
      >
        <button onClick={() => setBookingOpen(false)} className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-[#F1F5F9] transition-colors" aria-label="Close booking" data-testid="booking-modal-close">
          <X size={18} />
        </button>

        {done ? (
          <div className="py-20 px-8 text-center">
            <span className="w-16 h-16 rounded-full bg-[#E0F2FE] flex items-center justify-center mx-auto">
              <CheckCircle2 size={30} className="text-[#1D4ED8]" />
            </span>
            <h3 className="mt-5 font-display font-extrabold text-2xl">You're booked in.</h3>
            <p className="mt-2 text-sm text-[#475569] max-w-sm mx-auto">
              Thanks, {form.name.split(" ")[0]}! We've received your {isQuote ? "quote request" : "booking"} and will confirm by email shortly.
              {!isQuote && date && ` Selected slot: ${date.toDateString()} at ${slotLabel(time)}.`}
            </p>
            <button className="btn btn-primary mt-8" onClick={() => setBookingOpen(false)} data-testid="booking-modal-done-button">Done</button>
          </div>
        ) : (
          <div className="grid md:grid-cols-[240px_1fr]">
            <div className="hidden md:flex flex-col justify-between p-8 text-white" style={{ background: "linear-gradient(165deg,#0D1F3C,#0A1628)" }}>
              <div>
                <img src="/logo-full.png" alt="TrustDesk" style={{ height: 36, width: "auto" }} />
                <h3 className="mt-6 font-display font-extrabold text-2xl leading-snug">
                  {isQuote ? "Get your quote" : "Book a Free Consultation"}
                </h3>
                <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                  {isQuote ? "Pick a slot for a quick scoping call — we'll follow up with a tailored quote."
                    : "Pick a slot that works for you. We'll call you at the time you choose. It's completely free."}
                </p>
              </div>
              <div className="space-y-3 text-xs text-slate-400">
                <div className="flex items-center gap-2"><CalendarCheck2 size={14} className="text-cyan-400" /> Available 7 days a week</div>
                <div className="flex items-center gap-2"><Clock size={14} className="text-cyan-400" /> Free 30-minute consultation</div>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {step === 0 ? (
                <div>
                  <h4 className="font-display font-bold text-lg">Select a date & time</h4>
                  <div className="mt-4 flex flex-col lg:flex-row gap-5">
                    <div className="td-calendar rounded-xl border border-[rgba(15,23,42,0.08)] p-3 mx-auto lg:mx-0" data-testid="booking-calendar">
                      <DayPicker
                        mode="single"
                        selected={date}
                        onSelect={(d) => { setDate(d); setTime(""); }}
                        disabled={disabledDays}
                        fromDate={new Date()}
                        weekStartsOn={1}
                        classNames={{ nav_button: "p-1 hover:bg-[#F1F5F9] rounded" }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col" data-testid="booking-time-slots">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="eyebrow text-[9px] text-[#64748B]">Available times (all hours)</div>
                        {time && (
                          <span className="text-[11px] font-bold text-[#1D6FF2] bg-[rgba(29,111,242,0.08)] rounded-full px-2.5 py-1 whitespace-nowrap" data-testid="booking-selected-time">
                            {slotLabel(time)} selected
                          </span>
                        )}
                      </div>
                      <div className="booking-slots-scroll grid grid-cols-2 gap-2.5 max-h-[320px] overflow-y-auto pr-1.5">
                        {slots.map((s) => (
                          <button
                            key={s}
                            onClick={() => setTime(s)}
                            className={`flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-3 rounded-full border transition-all duration-200 ${time === s ? "bg-[#1D6FF2] text-white border-[#1D6FF2] shadow-[0_6px_18px_rgba(29,111,242,0.35)]" : "bg-white border-[rgba(15,23,42,0.12)] hover:border-[#1D6FF2] hover:bg-[rgba(29,111,242,0.05)] text-[#334155]"}`}
                            data-testid={`booking-slot-${s}`}
                          >
                            <Clock size={13} className={time === s ? "text-white" : "text-[#94A3B8]"} />
                            <span className="whitespace-nowrap">{slotLabel(s)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary w-full mt-6"
                    disabled={!date || !time}
                    onClick={() => setStep(1)}
                    style={{ opacity: !date || !time ? 0.4 : 1, cursor: !date || !time ? "not-allowed" : "pointer" }}
                    data-testid="booking-modal-next-button"
                  >
                    Continue <ArrowRight size={16} />
                  </button>
                </div>
              ) : (
                <div>
                  <button onClick={() => setStep(0)} className="flex items-center gap-1.5 text-xs font-semibold text-[#64748B] hover:text-[#0F172A] transition-colors" data-testid="booking-modal-back-button">
                    <ArrowLeft size={14} /> Back to calendar
                  </button>
                  <h4 className="mt-3 font-display font-bold text-lg">Your details</h4>
                  <div className="mt-4 grid sm:grid-cols-2 gap-3">
                    <input className="booking-input" placeholder="Full name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="booking-input-name" />
                    <input className="booking-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="booking-input-email" />
                    <input className="booking-input" placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="booking-input-company" />
                    <input className="booking-input" placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="booking-input-country" />
                    {isQuote ? (
                      <input className="booking-input sm:col-span-2" value={bookingPkg || ""} readOnly data-testid="booking-input-package" />
                    ) : (
                      <select className="booking-input sm:col-span-2" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} data-testid="booking-input-service">
                        <option value="">Service needed *</option>
                        {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    )}
                    <textarea className="booking-input sm:col-span-2" rows={3} placeholder="Short message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="booking-input-message" />
                  </div>
                  {error && <div className="mt-3 text-sm text-red-500" data-testid="booking-error">{error}</div>}
                  <button className="btn btn-primary w-full mt-5" onClick={submit} disabled={busy} data-testid="booking-modal-submit-button">
                    {busy ? "Sending..." : isQuote ? "Request Quote" : "Confirm Booking"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
